"use client"
import React, { useState, useEffect, useMemo, useRef } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import BuddiesGridTab from "../../components/social/BuddiesGridTab"
import PostFeed from "../../components/social/posts/PostFeed"
import PostModal from "../../components/modals/PostModal"
import CreateChannelModal from "../../components/channels/modals/CreateChannelModal"
import { userAPI, channelsAPI, postsAPI, socialUtils, clinicAPI, petAPI, handleApiError } from "../../services/api"
import { useAuth } from "../../hooks/useAuth"
import { usePostModal } from "../../contexts/PostModalContext"
import { useTranslation } from "react-i18next"
import { resolveImageUrl, getUserPlaceholderImage } from "../../utils/imageUtils"
import UserAverageRating from "../../components/social/UserAverageRating"
import "./userProfileStyle.css"

const UserProfile = () => {
  const { userType, profileId, postId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { t, i18n } = useTranslation()
  const { user, updateUserData, getUserType } = useAuth()
  const { openPostModal, closePostModal, isOpen: isPostModalOpen, selectedPostId } = usePostModal()
  const isRTL = i18n.dir() === "rtl"

  const prevIsPostModalOpen = useRef(isPostModalOpen);

  // Helper function to normalize user type
  const normalizeUserType = (userTypeValue) => {
    if (!userTypeValue) return null
    const normalized = String(userTypeValue).toUpperCase()
    // Map legacy and lowercase variants to SERVICE_PROVIDER
    if (normalized === 'PET_SHOP_OWNER' || normalized === 'SERVICE_PROVIDER' ||
      userTypeValue === 'service_provider' || userTypeValue === 'pet_shop_owner') {
      return 'SERVICE_PROVIDER'
    }
    return normalized
  }

  const getVisibilityStorageKey = (id) => `profile_visibility_${id || "self"}`

  const loadDetailVisibility = (id) => {
    try {
      const raw = localStorage.getItem(getVisibilityStorageKey(id))
      if (!raw) return null
      const parsed = JSON.parse(raw)
      return parsed
    } catch (e) {
      console.warn("Failed to load visibility preferences", e)
      return null
    }
  }

  const saveDetailVisibility = (id, visibility) => {
    try {
      localStorage.setItem(getVisibilityStorageKey(id), JSON.stringify(visibility))
    } catch (e) {
      console.warn("Failed to save visibility preferences", e)
    }
  }

  // Tab mapping
  const TAB_MAP = useMemo(() => ({
    'profile': 1,
    'buddies': 2,
    'pets': 3,
    'communities': 4,
    'media': 5,
    'posts': 6
  }), []);

  const REVERSE_TAB_MAP = useMemo(() => ({
    1: 'profile',
    2: 'buddies',
    3: 'pets',
    4: 'communities',
    5: 'media',
    6: 'posts'
  }), []);

  // Active tab (1-6) - initialize from URL path
  const [activeTab, setActiveTab] = useState(() => {
    const path = window.location.pathname;
    const parts = path.split('/');
    if (parts.includes('buddies')) return 2;
    if (parts.includes('pets')) return 3;
    if (parts.includes('communities')) return 4;
    if (parts.includes('media')) return 5;
    if (parts.includes('posts')) return 6;
    return 1;
  })

  // Profile data
  const [profileData, setProfileData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  // Communities
  const [communities, setCommunities] = useState([])
  const [isLoadingCommunities, setIsLoadingCommunities] = useState(false)

  // Clinics count (for veterinarians)
  const [clinicsCount, setClinicsCount] = useState(0)

  // Communities count
  const [communitiesCount, setCommunitiesCount] = useState(0)

  // Posts and media for Media / Posts tabs
  const [posts, setPosts] = useState([])
  const [allPosts, setAllPosts] = useState([]) // Store all fetched posts
  const [profilePosts, setProfilePosts] = useState(null) // Posts from profile response (optimization)
  const [mediaItems, setMediaItems] = useState([])
  const [isLoadingMedia, setIsLoadingMedia] = useState(false)
  const [isLoadingPosts, setIsLoadingPosts] = useState(false)
  const [isLoadingMorePosts, setIsLoadingMorePosts] = useState(false)
  const [hasMorePosts, setHasMorePosts] = useState(false)
  const [postsPerPage] = useState(10) // Server-side pagination: 10 posts per page
  const [currentPostsPage, setCurrentPostsPage] = useState(1)

  // Pagination metadata from backend
  const [postsPagination, setPostsPagination] = useState(null)
  const [petsPagination, setPetsPagination] = useState(null)
  const [currentPetsPage, setCurrentPetsPage] = useState(1)
  const [isLoadingMorePets, setIsLoadingMorePets] = useState(false)
  const [hasMorePets, setHasMorePets] = useState(false)

  // Pets search (Profile > Pets tab)
  const [isPetsSearchOpen, setIsPetsSearchOpen] = useState(false)
  const [petsSearchQuery, setPetsSearchQuery] = useState("")

  // Communities search state
  const [isCommunitiesSearchOpen, setIsCommunitiesSearchOpen] = useState(false)
  const [communitiesSearchQuery, setCommunitiesSearchQuery] = useState("")

  // PostModal state (for creating/editing posts)
  const [isCreateEditModalOpen, setIsCreateEditModalOpen] = useState(false)
  const [editingPostData, setEditingPostData] = useState(null)
  const [selectedPrivacy, setSelectedPrivacy] = useState("everyone")
  const [pendingFileType, setPendingFileType] = useState(null)

  // Bio editing state
  const [isEditingBio, setIsEditingBio] = useState(false)
  const [tempBio, setTempBio] = useState("")
  const [isUpdatingBio, setIsUpdatingBio] = useState(false)

  // Detail visibility (public/private per field)
  const [detailVisibility, setDetailVisibility] = useState({
    phone: true,
    gender: true,
    age: true,
    country: true,
    city: true,
    name_en: true,
    legal_number: true,
    contact_info: true,
    address: true,
  })

  // Avatar editing state
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false)
  const [avatarPreview, setAvatarPreview] = useState(null)
  const [avatarUploadError, setAvatarUploadError] = useState(null)

  // Success message state
  const [successMessage, setSuccessMessage] = useState(null)

  // Connects modal state
  const [isConnectsModalOpen, setIsConnectsModalOpen] = useState(false)

  // Create community modal state
  const [isCreateCommunityModalOpen, setIsCreateCommunityModalOpen] = useState(false)

  // Tab animation direction
  const [tabDirection, setTabDirection] = useState('right')

  // Fetch user profile data function (extracted for reuse)
  const loadProfileData = async () => {
    try {
      setIsLoading(true)
      setError(null)

      const token = localStorage.getItem("access_token")
      if (!token) {
        throw new Error("Authentication required")
      }

      const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000"

      // Fetch user profile with pagination parameters
      const profileResponse = await fetch(
        `${API_BASE_URL}/api/v1/user-profile?posts_page=1&posts_per_page=${postsPerPage}&pets_page=1&pets_per_page=10`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      )

      if (!profileResponse.ok) {
        throw new Error("Failed to fetch profile data")
      }

      const profileResult = await profileResponse.json()
      const userData = profileResult.data || profileResult

      if (!userData) {
        throw new Error("Invalid profile data received")
      }

      // Store pagination metadata from backend
      if (userData.posts_pagination) {
        setPostsPagination(userData.posts_pagination)
        setHasMorePosts(userData.posts_pagination.has_more_pages || false)
      }
      if (userData.pets_pagination) {
        setPetsPagination(userData.pets_pagination)
        setHasMorePets(userData.pets_pagination.has_more_pages || false)
      }

      // Use pets from profile response (already included in user-profile endpoint)
      // Fallback to separate API call if pets are not included OR if the list is empty
      let petsData = userData.pets && Array.isArray(userData.pets) ? userData.pets : []

      if (petsData.length === 0) {
        // Fallback: Fetch pets separately
        try {
          console.log("⚠️ No pets in profile data (or empty list), fetching explicitly...");
          const petsResponse = await petAPI.getMyPets();

          if (petsResponse.data) {
            const petsResult = petsResponse.data;
            const fetchedPets = Array.isArray(petsResult)
              ? petsResult
              : petsResult.data
                ? petsResult.data
                : []

            if (fetchedPets.length > 0) {
              petsData = fetchedPets;
              console.log(`✅ Fetched ${petsData.length} pets via fallback API`);
            }
          }
        } catch (petsError) {
          console.error("Error fetching pets:", petsError)
        }
      }

      // Use posts from profile response if available (optimization to avoid redundant API call)
      // Fallback to separate API call only if posts are not included in profile response
      let postsData = []
      let postsTotalCount = null

      if (userData.posts && Array.isArray(userData.posts) && userData.posts.length > 0) {
        // Posts are already included in the profile response (paginated)
        postsData = userData.posts
        // Store raw posts from profile for use in loadAllUserPosts
        // These posts are IN the user-profile response, not fetched separately
        setProfilePosts(userData.posts)
        console.log(`✅ Stored ${userData.posts.length} posts from user-profile response in profilePosts state`)
        // Use pagination total if available, otherwise fallback to posts_count
        postsTotalCount = userData.posts_pagination?.total || userData.posts_count || postsData.length
      } else {
        // No posts in profile response - backend should include posts when posts_page param is provided
        console.warn(`⚠️ No posts in user-profile response. userData.posts:`, userData.posts)
        console.warn(`⚠️ Backend should return posts array when posts_page parameter is provided`)

        // Fallback: Fetch posts separately if not included in profile (backward compatibility)
        // This should only happen if backend doesn't support pagination params yet
        try {
          const postsResponse = await fetch(`${API_BASE_URL}/api/v1/posts/my-posts?page=1&per_page=${postsPerPage}`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
          })

          if (postsResponse.ok) {
            const postsResult = await postsResponse.json()
            console.log("📥 Fallback my-posts response:", postsResult)

            // Handle different response structures
            if (postsResult.status === 'success' && postsResult.data) {
              // Laravel success response: { status: 'success', data: [...] }
              postsData = Array.isArray(postsResult.data) ? postsResult.data : (postsResult.data.data || [])
            } else if (postsResult.data) {
              // Direct data: { data: [...] }
              postsData = Array.isArray(postsResult.data)
                ? postsResult.data
                : postsResult.data.data
                  ? postsResult.data.data
                  : []
            } else if (Array.isArray(postsResult)) {
              // Array directly: [...]
              postsData = postsResult
            }

            // Store fallback posts in profilePosts so loadAllUserPosts can use them
            if (postsData.length > 0) {
              setProfilePosts(postsData)
              console.log(`✅ Fallback: Stored ${postsData.length} posts from my-posts endpoint`)
            } else {
              console.warn("⚠️ Fallback my-posts returned empty array. Response structure:", postsResult)
            }

            // Check if API response includes total count (for pagination)
            if (postsResult.pagination?.total !== undefined) {
              postsTotalCount = postsResult.pagination.total
            } else if (postsResult.meta?.total !== undefined) {
              postsTotalCount = postsResult.meta.total
            } else if (postsResult.total !== undefined) {
              postsTotalCount = postsResult.total
            } else if (postsResult.data?.total !== undefined) {
              postsTotalCount = postsResult.data.total
            }
          } else {
            console.error("⚠️ Fallback my-posts request failed:", postsResponse.status, postsResponse.statusText)
          }
        } catch (postsError) {
          console.error("Error fetching posts fallback:", postsError)
          setProfilePosts(null)
        }

        // If still no posts after fallback, set to null
        if (postsData.length === 0) {
          setProfilePosts(null)
          console.warn("⚠️ No posts found even after fallback fetch")
        } else {
          console.log(`✅ Fallback successful: ${postsData.length} posts will be processed`)
        }
      }

      // Calculate actual posts count
      // Priority: 1) API total count (if paginated), 2) Fetched posts length, 3) userData.posts_count
      const actualPostsCount =
        postsTotalCount !== null
          ? postsTotalCount
          : postsData.length > 0
            ? postsData.length
            : userData.posts_count || 0

      // Compute unified friends/connects (mutual connections) count
      // Backend now provides friends_count and stats.friends; keep fallbacks for safety.
      const friendsCount =
        userData.friends_count ??
        userData.connections_count ??
        userData.connects_count ??
        userData.followers_count ??
        userData.following_count ??
        0

      // Average rating calculation fallback
      let avgRating = userData.average_rating ?? userData.averageRating ?? 0;
      let countRating = userData.ratings_count ?? userData.ratingsCount ?? 0;

      // Fallback: Calculate from posts if backend rating is missing/zero but posts exist
      // Note: We check for 0/null because backend might return 0 even if posts have ratings (sync issue)
      if ((avgRating === null || avgRating === 0 || countRating === 0) && postsData.length > 0) {
        const ratedPosts = postsData.filter(p =>
          (p.average_rating !== null && p.average_rating !== undefined && parseFloat(p.average_rating) > 0) ||
          (p.avg_rating !== null && p.avg_rating !== undefined && parseFloat(p.avg_rating) > 0)
        );

        if (ratedPosts.length > 0) {
          const totalRating = ratedPosts.reduce((sum, p) =>
            sum + (parseFloat(p.average_rating ?? p.avg_rating) || 0), 0
          );
          avgRating = totalRating / ratedPosts.length;
          countRating = ratedPosts.length;

          console.log(`✅ UserProfile: Calculated frontend rating fallback: ${avgRating.toFixed(1)} (${countRating} posts)`);
        }
      }

      const transformedData = {
        id: userData.user_id || userData.id,
        name: userData.user_type === 'FIRM' ? (userData.display_name || userData.name_en || t("userprofile.me")) : (userData.display_name || userData.full_name || `${userData.first_name || ""} ${userData.last_name || ""}`.trim() || userData.username || "User"),
        username: userData.username || `@user${userData.user_id || userData.id}`,
        avatar: userData.profile_image_url || getUserPlaceholderImage(userData),
        bio: userData.bio || userData.description || "",
        postsCount: actualPostsCount,
        connectsCount: friendsCount,
        location: userData.full_address || userData.location || "",
        contact: userData.phone || userData.contact || null,
        email: userData.email || null,
        phone: userData.phone || userData.contact || userData.phone_number || null,
        legal_number: userData.legal_number || null,
        name_en: userData.name_en || null,
        address: userData.address || null,
        contact_info: userData.contact_info || null,
        gender: userData.gender || userData.sex || null,
        age: userData.age || userData.years_old || null,
        country:
          socialUtils.getLocalizedName(userData.country_data) ||
          socialUtils.getLocalizedName(userData.country) ||
          userData.country ||
          "",
        city:
          socialUtils.getLocalizedName(userData.city_data) ||
          socialUtils.getLocalizedName(userData.city) ||
          userData.city ||
          "",
        user_type: normalizeUserType(userData.user_type || userData.userType) || null,
        sub_type: userData.sub_type || userData.subType || null,
        sub_type_display_name: userData.sub_type_display_name || userData.subTypeDisplayName || null,

        // Stats from backend
        communitiesCount: userData.stats?.communities_count ?? 0,
        clinicsCount: userData.stats?.clinics_count ?? 0,

        // Visibility Settings (Default to true if missing)
        show_phone: userData.show_phone !== undefined ? userData.show_phone : true,
        show_gender: userData.show_gender !== undefined ? userData.show_gender : true,
        show_age: userData.show_age !== undefined ? userData.show_age : true,
        show_country: userData.show_country !== undefined ? userData.show_country : true,
        show_city: userData.show_city !== undefined ? userData.show_city : true,
        show_name_en: userData.show_name_en !== undefined ? userData.show_name_en : true,
        show_legal_number: userData.show_legal_number !== undefined ? userData.show_legal_number : true,
        show_contact_info: userData.show_contact_info !== undefined ? userData.show_contact_info : true,
        show_address: userData.show_address !== undefined ? userData.show_address : true,

        // Average rating from backend (calculated from all post ratings) - backwards compatible
        average_rating: avgRating,
        ratings_count: countRating,


        rating_label: userData.rating_label ?? userData.ratingLabel ?? null, // Optional: label from backend
        pets: petsData.map(pet => {
          let avatarUrl = "/images/no-image-pet.png"
          if (pet.primary_image_url) {
            avatarUrl = pet.primary_image_url
          } else if (pet.profile_image_path) {
            avatarUrl =
              typeof pet.profile_image_path === "string"
                ? pet.profile_image_path
                : pet.profile_image_path?.url ||
                pet.profile_image_path?.path ||
                "/images/no-image-pet.png"
          }
          return {
            id: pet.id,
            name: pet.name || t("userprofile.pets.unnamed"),
            avatar: avatarUrl,
            type: pet.species?.name || null,
          }
        }),
      }

      setProfileData(transformedData)
      setCommunitiesCount(transformedData.communitiesCount)
      setClinicsCount(transformedData.clinicsCount)
    } catch (err) {
      console.error("Error loading profile data:", err)
      setError(err.message || "Failed to load profile")
    } finally {
      setIsLoading(false)
    }
  }

  // Fetch user profile data on mount
  useEffect(() => {
    loadProfileData()
  }, [])

  // Initialize visibility state from profile data
  useEffect(() => {
    if (!profileData?.id) return

    setDetailVisibility(prev => ({
      ...prev,
      phone: profileData.show_phone,
      gender: profileData.show_gender,
      age: profileData.show_age,
      country: profileData.show_country,
      city: profileData.show_city,
      name_en: profileData.show_name_en,
      legal_number: profileData.show_legal_number,
      contact_info: profileData.show_contact_info,
      address: profileData.show_address,
    }))
  }, [profileData]) // Depend on profileData changes to sync settings

  // Listen for profile updates from other components (like EditProfileModal)
  useEffect(() => {
    const handleProfileUpdate = () => {
      // Refresh profile data when updated from other components
      loadProfileData()
    }

    // Listen for custom event when profile is updated
    window.addEventListener('profileUpdated', handleProfileUpdate)

    return () => {
      window.removeEventListener('profileUpdated', handleProfileUpdate)
    }
  }, [])

  // Sync activeTab with URL path changes
  useEffect(() => {
    const path = location.pathname;
    const parts = path.split('/');
    let newTab = 1;
    if (parts.includes('buddies')) newTab = 2;
    else if (parts.includes('pets')) newTab = 3;
    else if (parts.includes('communities')) newTab = 4;
    else if (parts.includes('media')) newTab = 5;
    else if (parts.includes('posts')) newTab = 6;

    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [location.pathname, activeTab]);

  // Sync post modal with URL postId
  useEffect(() => {
    // Don't reopen if modal was just closed (let the URL cleanup useEffect handle it)
    const wasJustClosed = prevIsPostModalOpen.current && !isPostModalOpen;

    // If we have a postId in the URL but the modal isn't open for it, open it
    // But only if the modal wasn't just closed (to prevent reopening after close)
    if (postId && !wasJustClosed && (!isPostModalOpen || String(selectedPostId) !== String(postId))) {
      openPostModal(postId);
    }
    // Don't auto-close modal when there's no postId in URL
    // This allows modal to stay open when opened programmatically (e.g., clicking nested post)
    // Modal will close when user clicks close button or navigates away (handled by other useEffect)
  }, [postId, selectedPostId, isPostModalOpen, openPostModal, closePostModal]);

  // Sync URL with post modal close (when closed via modal's own close button)
  useEffect(() => {
    // If the modal was JUST closed (transition from true to false) 
    // but the postId is still in the URL, that means the user clicked the close button
    if (prevIsPostModalOpen.current && !isPostModalOpen && postId) {
      const basePath = getBaseProfilePath();
      const tabName = REVERSE_TAB_MAP[activeTab];
      const newPath = activeTab === 1 ? basePath : `${basePath}/${tabName}`;
      navigate(newPath);
    }

    // Update the ref for the next render
    prevIsPostModalOpen.current = isPostModalOpen;
  }, [isPostModalOpen, postId, activeTab, navigate, REVERSE_TAB_MAP]);

  // Fetch user's communities
  useEffect(() => {
    const loadCommunities = async () => {
      if (!user?.id) return

      try {
        setIsLoadingCommunities(true)
        // Try to get all channels the user is a member of (not just owned)
        // If the backend only returns owned channels, we may need to use a different endpoint
        // or add a parameter like include_members: true
        const response = await channelsAPI.getMyChannels({
          page: 1,
          per_page: 100,
          include_members: true, // Request to include channels where user is a member (not just owner)
          role: 'all' // Request all roles (owner, admin, member)
        })

        const channelsData = response.data?.data || []

        // When viewing your own profile, show all communities
        // When viewing others' profiles, show only public communities
        const currentUserId = user?.id || user?.user_id
        const profileUserId = profileData?.id || profileData?.user_id
        const isOwnProfile = currentUserId && profileUserId && String(currentUserId) === String(profileUserId)
        const filteredChannels = isOwnProfile
          ? channelsData // Show all for own profile
          : channelsData.filter(channel => {
            const privacy = channel.privacy_setting || channel.privacySetting
            return privacy === 'public'
          })

        const processedCommunities = filteredChannels
          .map(channel => {
            const coverImageUrl = resolveImageUrl(
              channel.cover_image_url,
              channel.cover_image,
              channel.coverImageUrl,
              channel.coverImage,
              channel.cover_image_path,
              channel.images?.cover,
              channel.media?.cover
            )
            const profileImageUrl = resolveImageUrl(
              channel.profile_image_url,
              channel.profile_image,
              channel.profileImageUrl,
              channel.profileImage,
              channel.profile_image_path,
              channel.images?.profile,
              channel.media?.profile
            )

            return {
              id: channel.id,
              name: channel.name,
              description: channel.description || "",
              coverImage: profileImageUrl || "/images/social/pic-placeholder.png",
              profileImage: profileImageUrl || "/images/social/pic-placeholder.png",
              memberCount: channel.members_count || channel.memberCount || 0,
              postCount: channel.posts_count || channel.postCount || 0,
            }
          })

        setCommunities(processedCommunities)
        // Only update count if not already set from profile stats (profile stats is more authoritative)
        setCommunitiesCount(prev => prev || processedCommunities.length)
      } catch (err) {
        console.error("Error loading communities:", err)
      } finally {
        setIsLoadingCommunities(false)
      }
    }

    loadCommunities()
  }, [user?.id, profileData?.id])

  // Fetch user's posts and extract media for Tab 2 and Tab 3
  // Use paginated posts from profile response (server-side pagination)
  // Posts are already included in the profile response, so we just process them
  useEffect(() => {
    const loadAllUserPosts = async () => {
      if (!profileData?.id) return

      setIsLoadingMedia(true)
      setIsLoadingPosts(true)
      try {
        let fetchedPosts = []

        // Posts are already included in the user-profile response (from loadProfileData)
        // We use profilePosts which was set from userData.posts in the profile response
        // This means posts come FROM the profile endpoint, not a separate API call
        if (profilePosts && Array.isArray(profilePosts) && profilePosts.length > 0) {
          // Use posts from profile response - these are IN the user-profile response
          fetchedPosts = [...profilePosts]
          console.log(`✅ Using ${fetchedPosts.length} posts from user-profile response (not a separate API call)`)
        } else {
          // Backend should include posts in user-profile response when pagination params are provided
          console.warn("⚠️ No posts in user-profile response - ensure backend includes posts array when posts_page param is provided")
          fetchedPosts = []
        }

        // Transform posts to match PostFeed component's expected structure
        // Preserve raw post data before transformation for repost_id lookup
        const transformedPosts = fetchedPosts
          .map((rawPost) => {
            const transformed = socialUtils.transformPostData(rawPost);
            if (transformed) {
              // Ensure repost_id and parent_post_id are preserved from raw data
              // (transformPostData should handle this, but add safety check)
              if (!transformed.repost_id && rawPost.repost_id) {
                transformed.repost_id = rawPost.repost_id;
              }
              if (!transformed.parent_post_id && rawPost.parent_post_id) {
                transformed.parent_post_id = rawPost.parent_post_id;
              }
              // Store reference to raw post for debugging
              transformed._rawPost = rawPost;
            }
            return transformed;
          })
          .filter((post) => post !== null)
          .sort((a, b) => {
            // Sort by creation date, newest first
            const dateA = new Date(a.created_at || a.timestamp);
            const dateB = new Date(b.created_at || b.timestamp);
            return dateB - dateA;
          });

        // Fetch missing original posts for reposts
        // Check both repost_id and parent_post_id (backend may use either)
        const postsWithMissingRepostData = transformedPosts.filter(p => {
          const isRepost = p.is_repost || p.repost_id || p.type === 'repost' || p.parent_post_id;
          const hasRepostId = p.repost_id || p.parent_post_id;
          const missingRepostData = (!p.reposted_post || Object.keys(p.reposted_post).length === 0) &&
            (!p.original_post || Object.keys(p.original_post).length === 0);
          return isRepost && missingRepostData && hasRepostId;
        });

        if (postsWithMissingRepostData.length > 0) {
          console.log(`📥 Fetching ${postsWithMissingRepostData.length} missing parent posts for reposts...`);

          // Fetch original posts in parallel
          const fetchPromises = postsWithMissingRepostData.map(async (repostPost) => {
            try {
              // Use repost_id or parent_post_id (whichever is available)
              const originalPostId = repostPost.repost_id || repostPost.parent_post_id;

              if (!originalPostId) {
                console.warn(`⚠️ Repost ${repostPost.id} has no repost_id or parent_post_id`);
                return;
              }

              const originalPostResponse = await postsAPI.getPost(originalPostId);

              // Handle different response structures
              let originalPostData = null;
              if (originalPostResponse?.data) {
                if (originalPostResponse.data.status === 'success' && originalPostResponse.data.data) {
                  originalPostData = originalPostResponse.data.data;
                } else if (originalPostResponse.data.data) {
                  originalPostData = originalPostResponse.data.data;
                } else if (originalPostResponse.data.id) {
                  originalPostData = originalPostResponse.data;
                }
              } else if (originalPostResponse?.id) {
                originalPostData = originalPostResponse;
              }

              if (originalPostData) {
                // Transform the original post data
                const transformedOriginalPost = socialUtils.transformPostData(originalPostData);

                if (transformedOriginalPost && transformedOriginalPost.author) {
                  // Ensure author data is properly set
                  const author = transformedOriginalPost.author;

                  // Update the repost post with the fetched original post data
                  repostPost.reposted_post = {
                    id: transformedOriginalPost.id,
                    author: {
                      id: author.id || 0,
                      name: author.name ||
                        (author.first_name && author.last_name
                          ? `${author.first_name} ${author.last_name}`
                          : author.username || 'Unknown User'),
                      username: author.username || 'unknown',
                      avatar: author.avatar || author.image || getUserPlaceholderImage(author),
                      userType: author.userType || author.user_type || 'PET_GUARDIAN',
                    },
                    content: transformedOriginalPost.content || '',
                    image: transformedOriginalPost.image || null,
                    media: transformedOriginalPost.media || [],
                    timestamp: transformedOriginalPost.timestamp || transformedOriginalPost.created_at,
                    created_at: transformedOriginalPost.created_at,
                    reactions_count: transformedOriginalPost.reactions?.likes || transformedOriginalPost.reactions_count || 0,
                    comments_count: transformedOriginalPost.reactions?.comments || transformedOriginalPost.comments_count || 0,
                    reposts_count: transformedOriginalPost.reposts_count || 0,
                    shares_count: transformedOriginalPost.reactions?.shares || transformedOriginalPost.shares_count || 0,
                    reactions: transformedOriginalPost.reactions || {
                      likes: transformedOriginalPost.reactions_count || 0,
                      comments: transformedOriginalPost.comments_count || 0,
                      shares: transformedOriginalPost.shares_count || 0,
                    },
                  };
                  repostPost.original_post = repostPost.reposted_post; // Same for compatibility

                  console.log(`✅ Fetched parent post ${originalPostId} for repost ${repostPost.id}`, {
                    author: repostPost.reposted_post.author.name
                  });
                } else {
                  console.warn(`⚠️ Failed to transform original post ${originalPostId} for repost ${repostPost.id}`);
                }
              } else {
                console.warn(`⚠️ No data received for original post ${originalPostId} (repost ${repostPost.id})`);
              }
            } catch (error) {
              console.error(`❌ Error fetching original post ${repostPost.repost_id || repostPost.parent_post_id} for repost ${repostPost.id}:`, error);
            }
          });

          // Wait for all fetches to complete
          await Promise.all(fetchPromises);
        }

        // Store all posts (with fetched original posts if they were missing)
        setAllPosts(transformedPosts);

        // Display all posts from current page (server-side pagination)
        // No need to slice - backend already returns only the requested page
        setPosts(transformedPosts);
        setCurrentPostsPage(1);

        // hasMorePosts is already set from pagination metadata in loadProfileData

        // Extract all media from ALL posts (for media tab)
        // Backend should return empty media arrays for reposts, but add safety check just in case
        const allMedia = []
        transformedPosts.forEach(post => {
          // Safety check: Skip reposts entirely (backend should return empty media, but filter just in case)
          const isRepost = post.is_repost ||
            post.repost_id ||
            post.type === 'repost' ||
            (post.reposted_post && Object.keys(post.reposted_post).length > 0) ||
            (post.original_post && Object.keys(post.original_post).length > 0);

          // Only include media from regular posts (not reposts)
          if (!isRepost && post.media && Array.isArray(post.media) && post.media.length > 0) {
            post.media.forEach(mediaItem => {
              allMedia.push({
                ...mediaItem,
                postId: post.id,
                postContent: post.content,
                createdAt: post.created_at || post.createdAt
              })
            })
          }
        })

        setMediaItems(allMedia)
      } catch (err) {
        console.error("Error loading posts:", err)
      } finally {
        setIsLoadingMedia(false)
        setIsLoadingPosts(false)
      }
    }

    if (profileData?.id) {
      loadAllUserPosts()
    }
  }, [profileData?.id, profilePosts, postsPerPage])

  // Load more posts function - fetches next page from API
  const loadMorePosts = async () => {
    if (isLoadingMorePosts || !hasMorePosts) return

    setIsLoadingMorePosts(true)
    try {
      const token = localStorage.getItem("access_token")
      if (!token) {
        setIsLoadingMorePosts(false)
        return
      }

      const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000"
      const nextPage = currentPostsPage + 1

      // Fetch next page of posts from profile endpoint
      const response = await fetch(
        `${API_BASE_URL}/api/v1/user-profile?posts_page=${nextPage}&posts_per_page=${postsPerPage}&pets_page=1&pets_per_page=10`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      )

      if (response.ok) {
        const result = await response.json()
        const userData = result.data || result

        if (userData.posts && Array.isArray(userData.posts)) {
          // Transform new posts
          const transformedNewPosts = userData.posts
            .map(socialUtils.transformPostData)
            .filter((post) => post !== null)

          // Fetch missing parent_post data for reposts (same logic as initial load)
          const postsWithMissingRepostData = transformedNewPosts.filter(p =>
            (p.is_repost || p.repost_id || p.type === 'repost' || p.parent_post_id) &&
            (!p.reposted_post || Object.keys(p.reposted_post).length === 0) &&
            (!p.original_post || Object.keys(p.original_post).length === 0) &&
            (p.repost_id || p.parent_post_id)
          )

          if (postsWithMissingRepostData.length > 0) {
            const fetchPromises = postsWithMissingRepostData.map(async (repostPost) => {
              try {
                const originalPostId = repostPost.repost_id || repostPost.parent_post_id
                if (!originalPostId) return

                const originalPostResponse = await postsAPI.getPost(originalPostId)
                let originalPostData = null
                if (originalPostResponse?.data) {
                  if (originalPostResponse.data.status === 'success' && originalPostResponse.data.data) {
                    originalPostData = originalPostResponse.data.data
                  } else if (originalPostResponse.data.data) {
                    originalPostData = originalPostResponse.data.data
                  } else if (originalPostResponse.data.id) {
                    originalPostData = originalPostResponse.data
                  }
                } else if (originalPostResponse?.id) {
                  originalPostData = originalPostResponse
                }

                if (originalPostData) {
                  const transformedOriginalPost = socialUtils.transformPostData(originalPostData)
                  if (transformedOriginalPost && transformedOriginalPost.author) {
                    const author = transformedOriginalPost.author
                    repostPost.reposted_post = {
                      id: transformedOriginalPost.id,
                      author: {
                        id: author.id || 0,
                        name: author.name ||
                          (author.first_name && author.last_name
                            ? `${author.first_name} ${author.last_name}`
                            : author.username || 'Unknown User'),
                        username: author.username || 'unknown',
                        avatar: author.avatar || author.image || getUserPlaceholderImage(author),
                        userType: author.userType || author.user_type || 'PET_GUARDIAN',
                      },
                      content: transformedOriginalPost.content || '',
                      image: transformedOriginalPost.image || null,
                      media: transformedOriginalPost.media || [],
                      timestamp: transformedOriginalPost.timestamp || transformedOriginalPost.created_at,
                      created_at: transformedOriginalPost.created_at,
                      reactions_count: transformedOriginalPost.reactions?.likes || transformedOriginalPost.reactions_count || 0,
                      comments_count: transformedOriginalPost.reactions?.comments || transformedOriginalPost.comments_count || 0,
                      reposts_count: transformedOriginalPost.reposts_count || 0,
                      shares_count: transformedOriginalPost.reactions?.shares || transformedOriginalPost.shares_count || 0,
                      reactions: transformedOriginalPost.reactions || {
                        likes: transformedOriginalPost.reactions_count || 0,
                        comments: transformedOriginalPost.comments_count || 0,
                        shares: transformedOriginalPost.shares_count || 0,
                      },
                    }
                    repostPost.original_post = repostPost.reposted_post
                  }
                }
              } catch (error) {
                console.error(`Error fetching original post for repost ${repostPost.id}:`, error)
              }
            })
            await Promise.all(fetchPromises)
          }

          // Append new posts to existing ones
          setPosts(prevPosts => [...prevPosts, ...transformedNewPosts])
          setAllPosts(prevAllPosts => [...prevAllPosts, ...transformedNewPosts])
          setCurrentPostsPage(nextPage)

          // Update pagination metadata
          if (userData.posts_pagination) {
            setPostsPagination(userData.posts_pagination)
            setHasMorePosts(userData.posts_pagination.has_more_pages || false)
          } else {
            setHasMorePosts(false)
          }
        }
      }
    } catch (error) {
      console.error("Error loading more posts:", error)
    } finally {
      setIsLoadingMorePosts(false)
    }
  }

  // Load more pets function - fetches next page from API
  const loadMorePets = async () => {
    if (isLoadingMorePets || !hasMorePets) return

    setIsLoadingMorePets(true)
    try {
      const token = localStorage.getItem("access_token")
      if (!token) {
        setIsLoadingMorePets(false)
        return
      }

      const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000"
      const nextPage = currentPetsPage + 1

      // Fetch next page of pets from profile endpoint
      const response = await fetch(
        `${API_BASE_URL}/api/v1/user-profile?posts_page=1&posts_per_page=${postsPerPage}&pets_page=${nextPage}&pets_per_page=10`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      )

      if (response.ok) {
        const result = await response.json()
        const userData = result.data || result

        if (userData.pets && Array.isArray(userData.pets)) {
          // Append new pets to existing ones
          setProfileData(prevData => ({
            ...prevData,
            pets: [...(prevData.pets || []), ...userData.pets.map(pet => {
              let avatarUrl = "/images/no-image-pet.png"
              if (pet.primary_image_url) {
                avatarUrl = pet.primary_image_url
              } else if (pet.profile_image_path) {
                avatarUrl =
                  typeof pet.profile_image_path === "string"
                    ? pet.profile_image_path
                    : pet.profile_image_path?.url ||
                    pet.profile_image_path?.path ||
                    "/images/no-image-pet.png"
              }
              return {
                id: pet.id,
                name: pet.name || t("userprofile.pets.unnamed"),
                avatar: avatarUrl,
                type: pet.species?.name || null,
              }
            })]
          }))
          setCurrentPetsPage(nextPage)

          // Update pagination metadata
          if (userData.pets_pagination) {
            setPetsPagination(userData.pets_pagination)
            setHasMorePets(userData.pets_pagination.has_more_pages || false)
          } else {
            setHasMorePets(false)
          }
        }
      }
    } catch (error) {
      console.error("Error loading more pets:", error)
    } finally {
      setIsLoadingMorePets(false)
    }
  }

  // Fetch clinics count for veterinarians
  useEffect(() => {
    const loadClinicsCount = async () => {
      // Only check for the current logged-in user (not the profile being viewed)
      // Only fetch for veterinarians
      const currentUserType = getUserType() || normalizeUserType(user?.user_type || userType)
      if (!currentUserType || (currentUserType !== 'VETERINARIAN' && currentUserType !== 'VET')) {
        setClinicsCount(0)
        return
      }

      // Only show clinics if viewing own profile
      if (!isCurrentUserProfile()) {
        setClinicsCount(0)
        return
      }

      try {
        const token = localStorage.getItem("access_token")
        if (!token) {
          setClinicsCount(0)
          return
        }

        // Use clinicAPI instead of direct fetch
        const response = await clinicAPI.getClinicSetupData()

        if (response.data?.status === 'success') {
          const clinics = response.data.data?.owned_clinics || []
          const count = Array.isArray(clinics) ? clinics.length : 0
          // Only show if more than 1 clinic
          setClinicsCount(count > 0 ? count : 0)

          // Update localStorage with fresh data
          if (Array.isArray(clinics)) {
            try {
              localStorage.setItem('owned_clinics', JSON.stringify(clinics))
            } catch (e) {
              console.error('Error saving owned_clinics to localStorage:', e)
            }
          }
        } else {
          setClinicsCount(0)
        }
      } catch (err) {
        console.error("Error loading clinics count:", err)
        setClinicsCount(0)
      }
    }

    // Load if we have user data and it's the current user's profile
    // But if we already have a count from profile stats, we might not need to fetch
    if (user && profileData && isCurrentUserProfile() && !profileData.clinicsCount) {
      loadClinicsCount()
    } else if (!profileData) {
      // If no profile data yet, reset count
      setClinicsCount(0)
    }
  }, [profileData, user, userType])

  // Helper to get the canonical base profile path
  const getBaseProfilePath = () => {
    if (profileId) {
      return location.pathname.startsWith('/social/profile')
        ? `/social/profile/${profileId}`
        : `/profile/${profileId}`;
    }
    return '/social/profile';
  };

  // Navigate between tabs
  const goToTab = (tabNumber) => {
    if (tabNumber >= 1 && tabNumber <= 6) {
      setTabDirection(tabNumber > activeTab ? 'left' : 'right')
      setActiveTab(tabNumber)

      // Tab 2 (buddies) - navigate to /buddies path
      if (tabNumber === 2) {
        const basePath = getBaseProfilePath();
        navigate(`${basePath}/buddies`);
        return;
      }

      const tabName = REVERSE_TAB_MAP[tabNumber];
      const basePath = getBaseProfilePath();

      const newPath = tabNumber === 1 ? basePath : `${basePath}/${tabName}`;
      navigate(newPath);
    }
  }

  const nextTab = () => {
    setTabDirection('left')
    setActiveTab(prev => prev < 6 ? prev + 1 : 1)
  }

  const prevTab = () => {
    setTabDirection('right')
    setActiveTab(prev => prev > 1 ? prev - 1 : 6)
  }

  // Post handlers for Tab 3
  const handleUpdatePost = async (postId, postData) => {
    try {
      // Call the API to update the post
      const response = await postsAPI.updatePost(postId, postData);

      // Get the updated post from response - handle nested structure
      const updatedPostData = response?.data?.data?.post || response?.data?.post || response?.data?.data || response?.data || response;

      // Transform the post data to match our structure
      const transformedPost = socialUtils.transformPostData(updatedPostData);

      if (transformedPost) {
        // Get the raw content from the API response (before transformation might strip it)
        const rawContent = updatedPostData?.content || transformedPost.content;

        // Create the updated post object
        const createUpdatedPost = (existingPost) => ({
          ...existingPost,
          ...transformedPost,
          // Ensure content is updated from raw API response
          content: rawContent !== undefined && rawContent !== null ? rawContent : (transformedPost.content !== undefined ? transformedPost.content : existingPost.content),
          // Preserve media items if they exist
          media: transformedPost.media && transformedPost.media.length > 0 ? transformedPost.media : (existingPost.media || []),
          // Preserve author data
          author: transformedPost.author || existingPost.author,
          // Preserve updated_at timestamp
          updated_at: transformedPost.updated_at || updatedPostData.updated_at || existingPost.updated_at,
          // Ensure hasMoreContent is recalculated
          hasMoreContent: rawContent && rawContent.length > 200,
        });

        // Update posts state - merge with existing post to preserve all fields
        setPosts(prevPosts => {
          const foundIndex = prevPosts.findIndex(post => String(post.id) === String(transformedPost.id));
          if (foundIndex === -1) {
            // Post not found, return as is
            return prevPosts;
          }
          // Create a new array with the updated post
          const updated = [...prevPosts];
          updated[foundIndex] = createUpdatedPost(prevPosts[foundIndex]);
          return updated;
        });

        // Also update allPosts
        setAllPosts(prevPosts => {
          const foundIndex = prevPosts.findIndex(post => String(post.id) === String(transformedPost.id));
          if (foundIndex === -1) {
            // Post not found, return as is
            return prevPosts;
          }
          // Create a new array with the updated post
          const updated = [...prevPosts];
          updated[foundIndex] = createUpdatedPost(prevPosts[foundIndex]);
          return updated;
        });

        // If it's a repost, refetch original post data if needed
        if (transformedPost.is_repost || transformedPost.repost_id) {
          // Check if original post data is missing
          if (!transformedPost.reposted_post || Object.keys(transformedPost.reposted_post).length === 0) {
            try {
              const originalPostResponse = await postsAPI.getPost(transformedPost.repost_id);
              const originalPostData = originalPostResponse?.data?.data || originalPostResponse?.data;
              if (originalPostData) {
                const transformedOriginalPost = socialUtils.transformPostData(originalPostData);
                if (transformedOriginalPost) {
                  transformedPost.reposted_post = {
                    id: transformedOriginalPost.id,
                    author: transformedOriginalPost.author,
                    content: transformedOriginalPost.content,
                    image: transformedOriginalPost.image,
                    media: transformedOriginalPost.media,
                    timestamp: transformedOriginalPost.timestamp,
                    created_at: transformedOriginalPost.created_at,
                    reactions_count: transformedOriginalPost.reactions?.likes || transformedOriginalPost.reactions_count || 0,
                    comments_count: transformedOriginalPost.reactions?.comments || transformedOriginalPost.comments_count || 0,
                    reposts_count: transformedOriginalPost.reposts_count || 0,
                    shares_count: transformedOriginalPost.reactions?.shares || transformedOriginalPost.shares_count || 0,
                    reactions: transformedOriginalPost.reactions || {
                      likes: transformedOriginalPost.reactions_count || 0,
                      comments: transformedOriginalPost.comments_count || 0,
                      shares: transformedOriginalPost.shares_count || 0,
                    },
                  };
                  transformedPost.original_post = transformedPost.reposted_post;

                  // Update state again with complete repost data
                  setPosts(prevPosts =>
                    prevPosts.map(post => {
                      if (String(post.id) === String(transformedPost.id)) {
                        return {
                          ...post,
                          ...transformedPost,
                          reposted_post: transformedPost.reposted_post,
                          original_post: transformedPost.original_post,
                        };
                      }
                      return post;
                    })
                  )
                  setAllPosts(prevPosts =>
                    prevPosts.map(post => {
                      if (String(post.id) === String(transformedPost.id)) {
                        return {
                          ...post,
                          ...transformedPost,
                          reposted_post: transformedPost.reposted_post,
                          original_post: transformedPost.original_post,
                        };
                      }
                      return post;
                    })
                  )
                }
              }
            } catch (error) {
              console.error('Error fetching original post after update:', error);
            }
          }
        }
      }

      // Close the modal after successful update
      closeCreateEditModal();
    } catch (error) {
      console.error('Error updating post:', error);
      throw error; // Re-throw so PostModal can handle it
    }
  }

  const handleDeletePost = async (post) => {
    const postId = post?.id || post;
    if (!postId) return;

    try {
      // Call API to delete the post
      await postsAPI.deletePost(postId);

      // Remove from state
      setPosts(prevPosts => prevPosts.filter(p => p.id !== postId))
      // Also update allPosts
      setAllPosts(prevPosts => prevPosts.filter(p => p.id !== postId))
      // Also update media items if needed
      setMediaItems(prevMedia => prevMedia.filter(media => media.postId !== postId))
      // Update hasMorePosts flag if needed
      setHasMorePosts(prev => {
        const remainingPosts = allPosts.filter(p => p.id !== postId)
        return remainingPosts.length > posts.length
      })
    } catch (error) {
      console.error('Error deleting post:', error);
      handleApiError(error);
    }
  }

  const handleEditPost = (post) => {
    // Open PostModal in edit mode directly in the profile page
    setEditingPostData(post);
    setPendingFileType(null);
    setIsCreateEditModalOpen(true);
  }

  const closeCreateEditModal = () => {
    setIsCreateEditModalOpen(false);
    setEditingPostData(null);
    setPendingFileType(null);
  }

  const handleCreatePost = async (postData) => {
    try {
      const response = await postsAPI.createPost(postData);
      const newPost = socialUtils.transformPostData(response?.data?.data || response?.data);
      if (newPost) {
        // Add new post to the beginning of the posts list
        setPosts(prevPosts => [newPost, ...prevPosts]);
        setAllPosts(prevPosts => [newPost, ...prevPosts]);
        // Update posts count
        if (profileData) {
          setProfileData(prev => ({
            ...prev,
            postsCount: (prev.postsCount || 0) + 1
          }));
        }
      }
      closeCreateEditModal();
    } catch (error) {
      console.error('Error creating post:', error);
      throw error;
    }
  }

  const handleLikeToggle = (postId, isLiked) => {
    setPosts(prevPosts =>
      prevPosts.map(post => {
        if (post.id === postId) {
          const likesCount = post.likes_count || post.likesCount || 0
          return {
            ...post,
            is_liked: isLiked,
            likes_count: isLiked ? likesCount + 1 : Math.max(0, likesCount - 1)
          }
        }
        return post
      })
    )
  }

  const handleCommentAdded = (postId) => {
    // Update comments count
    setPosts(prevPosts =>
      prevPosts.map(post => {
        if (post.id === postId) {
          const commentsCount = post.comments_count || post.commentsCount || 0
          return {
            ...post,
            comments_count: commentsCount + 1
          }
        }
        return post
      })
    )
  }

  // Helper function to check if this is the current user's profile
  const isCurrentUserProfile = () => {
    if (!user || !profileData) return true // Default to true for own profile
    const currentUserId = user.id || user.user_id
    const profileUserId = profileData.id
    return currentUserId && profileUserId && String(currentUserId) === String(profileUserId)
  }

  // Handle avatar selection
  const handleAvatarSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
      // Clear any previous errors
      setAvatarUploadError(null)
      setAvatarPreview(null) // Clear any previous preview

      // Validate file type
      if (!file.type.startsWith('image/')) {
        setAvatarUploadError(t("userprofile.errors.invalidImageType"))
        return
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setAvatarUploadError(t("userprofile.errors.invalidImageSize"))
        return
      }

      // Don't show preview yet - wait for successful upload
      // Just upload directly without preview to avoid showing failed image
      handleAvatarUpload(file)
    }
  }

  // Handle avatar upload
  const handleAvatarUpload = async (file) => {
    try {
      setIsUploadingAvatar(true)
      const token = localStorage.getItem("access_token")
      const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000"

      const formData = new FormData()
      formData.append('profile_image', file)

      const response = await fetch(`${API_BASE_URL}/api/v1/user-profile/upload-image`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        body: formData,
      })

      if (response.ok) {
        const result = await response.json()

        // Get the uploaded image URL from the response
        const uploadedImageUrl = result.imageUrl ||
          result.url ||
          result.data?.imageUrl ||
          result.data?.url ||
          result.data?.profile_image_url ||
          result.profile_image_url ||
          result.message?.imageUrl ||
          result.message?.url

        // Only update profile data if we got a valid URL from server
        if (uploadedImageUrl) {
          setProfileData(prev => ({
            ...prev,
            avatar: uploadedImageUrl,
          }))

          // Update the auth hook's user data with the new avatar
          if (updateUserData) {
            updateUserData({
              profile_image_url: uploadedImageUrl,
              avatar: uploadedImageUrl,
            })
          }
        }

        setSuccessMessage(t("userprofile.messages.avatarUpdated"))
        setTimeout(() => setSuccessMessage(null), 3000)
        setAvatarPreview(null)
        setAvatarUploadError(null) // Clear any previous errors
      } else {
        // Parse error response to get validation message
        let errorMessage = t("userprofile.errors.updateAvatar")
        try {
          const errorData = await response.json()
          // Extract validation error message from API response
          if (errorData.message) {
            errorMessage = errorData.message
          } else if (errorData.errors) {
            // Handle Laravel validation errors structure
            if (errorData.errors.profile_image) {
              const validationErrors = Array.isArray(errorData.errors.profile_image)
                ? errorData.errors.profile_image
                : [errorData.errors.profile_image]
              errorMessage = validationErrors[0] || errorMessage
            } else if (typeof errorData.errors === 'object') {
              // Try to get first error from errors object
              const firstErrorKey = Object.keys(errorData.errors)[0]
              if (firstErrorKey) {
                const firstError = errorData.errors[firstErrorKey]
                errorMessage = Array.isArray(firstError) ? firstError[0] : firstError
              }
            }
          } else if (errorData.error) {
            errorMessage = errorData.error
          }
        } catch (parseError) {
          // If we can't parse the error, use default message
          console.error("Error parsing API response:", parseError)
        }

        // Set avatar upload error (inline error, not page error)
        setAvatarUploadError(errorMessage)
        // Clear error after 5 seconds
        setTimeout(() => setAvatarUploadError(null), 5000)

        // Don't change avatar - keep original (no preview was shown, so no revert needed)
        setAvatarPreview(null)
      }
    } catch (error) {
      console.error("Error updating avatar:", error)
      // Set avatar upload error (inline error, not page error)
      const errorMessage = error.message || t("userprofile.errors.updateAvatar")
      setAvatarUploadError(errorMessage)
      // Clear error after 5 seconds
      setTimeout(() => setAvatarUploadError(null), 5000)
      setAvatarPreview(null)
      // Don't change avatar - keep original
    } finally {
      setIsUploadingAvatar(false)
    }
  }

  // Trigger avatar upload
  const triggerAvatarUpload = () => {
    const input = document.getElementById('avatar-upload-input')
    if (input) {
      input.click()
    }
  }

  // Bio editing functions
  const startEditingBio = () => {
    setIsEditingBio(true)
    setTempBio(profileData?.bio || "")
  }

  const cancelEditingBio = () => {
    setIsEditingBio(false)
    setTempBio("")
  }

  const handleBioUpdate = async () => {
    if (tempBio.trim() === profileData?.bio) {
      cancelEditingBio()
      return
    }

    try {
      setIsUpdatingBio(true)
      const token = localStorage.getItem("access_token")
      const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000"

      if (!token) {
        setError(t("userprofile.errors.authRequired"))
        return
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/user-profile`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          bio: tempBio.trim(),
        }),
      })

      if (response.ok) {
        // Update the profile data with the new bio
        setProfileData(prev => ({
          ...prev,
          bio: tempBio.trim(),
        }))

        setSuccessMessage(t("userprofile.messages.bioUpdated"))
        setTimeout(() => setSuccessMessage(null), 3000)
        setIsEditingBio(false)
        setTempBio("")
      } else {
        const errorText = await response.text()
        console.error("Failed to update bio:", errorText)
        setError(t("userprofile.errors.updateBio"))
        setTimeout(() => setError(null), 5000)
      }
    } catch (error) {
      console.error("Error updating bio:", error)
      setError(t("userprofile.errors.updateBioError"))
      setTimeout(() => setError(null), 5000)
    } finally {
      setIsUpdatingBio(false)
    }
  }

  // Get user type color
  const getUserTypeColor = (userType) => {
    const userTypeConfig = {
      'PET_GUARDIAN': '#8548A8',
      'VETERINARIAN': '#00D4B3',
      'SERVICE_PROVIDER': '#D65745',
      'CLINIC_MODERATOR': '#2A8AD2',
      'FIRM': '#1B3A6B', // Navy Blue
    }
    return userTypeConfig[userType] || '#8548A8'
  }

  // Normalize user type for color lookup (for current user's own profile, use getUserType; for profileData, normalize it)
  const profileUserType = profileData?.user_type
    ? normalizeUserType(profileData.user_type)
    : getUserType() || normalizeUserType(user?.user_type || userType)
  const userTypeColor = getUserTypeColor(profileUserType)

  if (isLoading) {
    return (
      <div
        className={`userprofile-root ${isRTL ? "rtl" : ""}`}
        dir={i18n.dir()}
        lang={i18n.language}
        style={{ '--user-type-color': userTypeColor }}
      >
        <div className="userprofile-loading">
          <div className="loading-spinner"></div>
          <p>{t("common.loading") || "Loading..."}</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`userprofile-root ${isRTL ? "rtl" : ""}`} dir={i18n.dir()} lang={i18n.language}>
        <div className="userprofile-error">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>
            {t("common.retry") || "Retry"}
          </button>
        </div>
      </div>
    )
  }

  if (!profileData) {
    return null
  }

  const hasProfileDetails = Boolean(
    profileData.contact ||
    profileData.phone ||
    profileData.gender ||
    profileData.age ||
    profileData.country ||
    profileData.city
  )

  const toggleFieldVisibility = async (fieldKey) => {
    // 1. Optimistic update
    const newVisibility = !detailVisibility[fieldKey];
    setDetailVisibility((prev) => ({ ...prev, [fieldKey]: newVisibility }));

    // 2. Map frontend key to backend column name
    const backendKey = `show_${fieldKey}`;

    try {
      // 3. Call User Profile Update API
      await userAPI.updateProfile({ [backendKey]: newVisibility });

      // 4. Update local profileData to keep it in sync (optional but good for consistency)
      setProfileData(prev => ({
        ...prev,
        [backendKey]: newVisibility
      }));

    } catch (error) {
      console.error("Failed to update visibility setting:", error);
      // Revert on error
      setDetailVisibility((prev) => ({ ...prev, [fieldKey]: !newVisibility }));
      // Ideally show a toast error here
    }
  }

  return (
    <div
      className={`userprofile-root ${isRTL ? "rtl" : ""}`}
      dir={i18n.dir()}
      lang={i18n.language}
      style={{ '--user-type-color': userTypeColor }}
    >
      {/* Success Message */}
      {successMessage && (
        <div className="userprofile-success-toast">
          <div className="userprofile-success-content">
            <span className="userprofile-success-text">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Full Width Layout */}
      <div className="userprofile-layout">
        {/* Left Sidebar Menu */}
        <div className="userprofile-sidebar-menu">
          {/* Profile Card */}
          <div
            className={`userprofile-menu-card ${activeTab === 1 ? 'active' : ''}`}
            onClick={() => goToTab(1)}
          >
            <div className="userprofile-menu-card-header">
              <h3 className="userprofile-menu-card-title">{t("userprofile.menu.myProfile") || "My Profile"}</h3>
            </div>
          </div>

          {/* Buddies Card */}
          <div
            className={`userprofile-menu-card ${activeTab === 2 ? 'active' : ''}`}
            onClick={() => goToTab(2)}
          >
            <div className="userprofile-menu-card-header">
              <h3 className="userprofile-menu-card-title">{t("userprofile.menu.myBuddies") || "My Buddies"}</h3>
            </div>
          </div>

          {/* Pets Card */}
          <div
            className={`userprofile-menu-card ${activeTab === 3 ? 'active' : ''}`}
            onClick={() => goToTab(3)}
          >
            <div className="userprofile-menu-card-header">
              <h3 className="userprofile-menu-card-title">{t("userprofile.menu.myPets") || "My Pets"}</h3>
            </div>
          </div>

          {/* Communities Card */}
          <div
            className={`userprofile-menu-card ${activeTab === 4 ? 'active' : ''}`}
            onClick={() => goToTab(4)}
          >
            <div className="userprofile-menu-card-header">
              <h3 className="userprofile-menu-card-title">{t("userprofile.menu.myCommunities") || "My Communities"}</h3>
            </div>
          </div>

          {/* Media Card */}
          <div
            className={`userprofile-menu-card ${activeTab === 5 ? 'active' : ''}`}
            onClick={() => goToTab(5)}
          >
            <div className="userprofile-menu-card-header">
              <h3 className="userprofile-menu-card-title">{t("userprofile.menu.myMedia") || "My Media"}</h3>
            </div>
          </div>

          {/* Posts Card */}
          <div
            className={`userprofile-menu-card ${activeTab === 6 ? 'active' : ''}`}
            onClick={() => goToTab(6)}
          >
            <div className="userprofile-menu-card-header">
              <h3 className="userprofile-menu-card-title">{t("userprofile.menu.myPosts") || "My Posts"}</h3>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="userprofile-main-content">
          {/* Profile Header Card - Always visible */}
          <div className="userprofile-header-card">
            {/* Hidden file input for avatar upload */}
            {isCurrentUserProfile() && (
              <input
                type="file"
                id="avatar-upload-input"
                accept="image/*"
                onChange={handleAvatarSelect}
                style={{ display: 'none' }}
              />
            )}

            {/* Top Section: User Info and Stats */}
            <div className="userprofile-header-top-section">
              {/* User Info on Left Side */}
              <div className="userprofile-header-user-info">
                <div className="userprofile-header-avatar-section">
                  <div
                    className={`userprofile-avatar-container ${isCurrentUserProfile() ? 'userprofile-avatar-editable' : ''}`}
                    onClick={isCurrentUserProfile() ? triggerAvatarUpload : undefined}
                    style={isCurrentUserProfile() ? { cursor: 'pointer' } : {}}
                  >
                    <img
                      src={profileData.avatar || getUserPlaceholderImage(profileData)}
                      alt={profileData.name}
                      className="userprofile-avatar"
                      onError={(e) => {
                        const placeholder = getUserPlaceholderImage(profileData);
                        if (e.target.src !== placeholder) {
                          e.target.src = placeholder
                        }
                      }}
                    />
                    {isCurrentUserProfile() && isUploadingAvatar && (
                      <div className="userprofile-avatar-uploading">
                        <div className="userprofile-avatar-spinner"></div>
                      </div>
                    )}
                    {isCurrentUserProfile() && !isUploadingAvatar && (
                      <div className="userprofile-avatar-overlay">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M11.3333 2.00004C11.5084 1.82498 11.7163 1.68612 11.9456 1.59134C12.1749 1.49656 12.4211 1.44775 12.67 1.44775C12.9189 1.44775 13.1651 1.49656 13.3944 1.59134C13.6237 1.68612 13.8316 1.82498 14.0067 2.00004C14.1817 2.1751 14.3206 2.383 14.4154 2.61231C14.5102 2.84162 14.559 3.08778 14.559 3.33671C14.559 3.58563 14.5102 3.83179 14.4154 4.0611C14.3206 4.29041 14.1817 4.49831 14.0067 4.67337L5.25998 13.42L1.33331 14.6667L2.57998 10.74L11.3333 2.00004Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                    )}
                  </div>
                  {avatarUploadError && (
                    <div className="userprofile-avatar-error">
                      {avatarUploadError}
                    </div>
                  )}
                </div>

                <div className="userprofile-header-info">
                  <h1 className="userprofile-name">{profileData.name}</h1>
                  {/* Show rating under name if user is NOT a rescuer */}
                  {profileData.sub_type !== 'rescuer' && profileData.average_rating !== null && profileData.average_rating !== undefined && (
                    <UserAverageRating
                      averageRating={profileData.average_rating}
                      ratingsCount={profileData.ratings_count || 0}
                      size="medium"
                    />
                  )}
                  {(profileData.sub_type_display_name || profileData.sub_type) && (
                    <p className="userprofile-subtype">
                      {profileData.sub_type_display_name || profileData.sub_type}
                    </p>
                  )}
                  {/* Show rating under subtype if user IS a rescuer */}
                  {profileData.sub_type === 'rescuer' && profileData.average_rating !== null && profileData.average_rating !== undefined && (
                    <UserAverageRating
                      averageRating={profileData.average_rating}
                      ratingsCount={profileData.ratings_count || 0}
                      ratingLabel={profileData.rating_label}
                      size="medium"
                    />
                  )}
                </div>
              </div>

              {/* Stat Cards on Right Side */}
              <div className="userprofile-header-stats-cards">
                <div className="userprofile-stat-square-card">
                  <div className="userprofile-stat-square-icon">🐾</div>
                  <div className="userprofile-stat-square-number">{profileData.pets?.length || 0}</div>
                  <div className="userprofile-stat-square-label">{t("userprofile.stats.pets") || "Pets"}</div>
                </div>
                <div className="userprofile-stat-square-card">
                  <div className="userprofile-stat-square-icon">📝</div>
                  <div className="userprofile-stat-square-number">{profileData.postsCount}</div>
                  <div className="userprofile-stat-square-label">{t("userprofile.stats.posts") || "Posts"}</div>
                </div>
                <div className="userprofile-stat-square-card">
                  <div className="userprofile-stat-square-icon">👥</div>
                  <div className="userprofile-stat-square-number">{profileData.connectsCount}</div>
                  <div className="userprofile-stat-square-label">{t("userprofile.stats.connects") || "Buddies"}</div>
                </div>
                <div className="userprofile-stat-square-card">
                  <div className="userprofile-stat-square-icon">🏘️</div>
                  <div className="userprofile-stat-square-number">{communitiesCount}</div>
                  <div className="userprofile-stat-square-label">{t("userprofile.stats.communities") || "Communities"}</div>
                </div>
                {(profileData?.user_type === 'VETERINARIAN' || profileData?.user_type === 'VET') && (
                  <div className="userprofile-stat-square-card">
                    <div className="userprofile-stat-square-icon">🏥</div>
                    <div className="userprofile-stat-square-number">{clinicsCount || 0}</div>
                    <div className="userprofile-stat-square-label">{t("userprofile.stats.clinics") || "Clinics"}</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile Tab Navbar - Only visible on mobile/tablet, positioned below header */}
          <div className="userprofile-mobile-navbar">
            <button
              className={`userprofile-mobile-nav-tab ${activeTab === 1 ? 'active' : ''}`}
              onClick={() => goToTab(1)}
            >
              {t("userprofile.menu.profile") || "Profile"}
            </button>
            <button
              className={`userprofile-mobile-nav-tab ${activeTab === 2 ? 'active' : ''}`}
              onClick={() => goToTab(2)}
            >
              {t("userprofile.menu.myBuddies") || "Buddies"}
            </button>
            <button
              className={`userprofile-mobile-nav-tab ${activeTab === 3 ? 'active' : ''}`}
              onClick={() => goToTab(3)}
            >
              {t("userprofile.menu.pets") || "Pets"}
            </button>
            <button
              className={`userprofile-mobile-nav-tab ${activeTab === 4 ? 'active' : ''}`}
              onClick={() => goToTab(4)}
            >
              {t("userprofile.menu.communities") || "Communities"}
            </button>
            <button
              className={`userprofile-mobile-nav-tab ${activeTab === 5 ? 'active' : ''}`}
              onClick={() => goToTab(5)}
            >
              {t("userprofile.menu.media") || "Media"}
            </button>
            <button
              className={`userprofile-mobile-nav-tab ${activeTab === 6 ? 'active' : ''}`}
              onClick={() => goToTab(6)}
            >
              {t("userprofile.menu.posts") || "Posts"}
            </button>
          </div>

          {/* Content based on active tab */}
          {activeTab === 1 && (
            <div className="userprofile-tab-panel">
              <div className="userprofile-section-card" style={{ minHeight: '1px' }}>
                {/* About Me Section */}
                <div className="userprofile-header-bio-section">
                  <div className="userprofile-bio-header">
                    <h3 className="userprofile-bio-title">{t("userprofile.sections.aboutMe")}</h3>
                    {isCurrentUserProfile() && !isEditingBio && (
                      <button
                        onClick={startEditingBio}
                        className="userprofile-bio-edit-btn"
                        title={t("userprofile.actions.editBio")}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M11.3333 2.00004C11.5084 1.82498 11.7163 1.68612 11.9456 1.59134C12.1749 1.49656 12.4211 1.44775 12.67 1.44775C12.9189 1.44775 13.1651 1.49656 13.3944 1.59134C13.6237 1.68612 13.8316 1.82498 14.0067 2.00004C14.1817 2.1751 14.3206 2.383 14.4154 2.61231C14.5102 2.84162 14.559 3.08778 14.559 3.33671C14.559 3.58563 14.5102 3.83179 14.4154 4.0611C14.3206 4.29041 14.1817 4.49831 14.0067 4.67337L5.25998 13.42L1.33331 14.6667L2.57998 10.74L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    )}
                  </div>
                  {isEditingBio ? (
                    <div className="userprofile-bio-edit-container">
                      <textarea
                        value={tempBio}
                        onChange={(e) => setTempBio(e.target.value)}
                        className="userprofile-bio-input"
                        placeholder={t("userprofile.bio.tellAboutYourself")}
                        rows={4}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && e.ctrlKey) {
                            handleBioUpdate()
                          } else if (e.key === "Escape") {
                            cancelEditingBio()
                          }
                        }}
                        autoFocus
                      />
                      <div className="userprofile-bio-edit-actions">
                        <button
                          onClick={handleBioUpdate}
                          disabled={isUpdatingBio}
                          className="userprofile-bio-save-btn"
                        >
                          {isUpdatingBio ? t("userprofile.buttons.saving") : t("userprofile.buttons.save")}
                        </button>
                        <button
                          onClick={cancelEditingBio}
                          disabled={isUpdatingBio}
                          className="userprofile-bio-cancel-btn"
                        >
                          {t("userprofile.buttons.cancel")}
                        </button>
                      </div>
                    </div>
                  ) : profileData.bio ? (
                    <p className="userprofile-bio">{profileData.bio}</p>
                  ) : isCurrentUserProfile() ? (
                    <button
                      onClick={startEditingBio}
                      className="userprofile-add-bio-btn"
                    >
                      {t("userprofile.bio.addAboutMe")}
                    </button>
                  ) : (
                    <p className="userprofile-bio-empty">{t("userprofile.sections.noBioAvailable")}</p>
                  )}
                </div>
                {hasProfileDetails && (
                  <div className="userprofile-profile-details">
                    <h4 className="userprofile-profile-details-title">
                      {t("userprofile.sections.profileDetails") || "Profile details"}
                    </h4>
                    <div className="userprofile-details-grid">
                      {(profileData.phone || profileData.contact) && (
                        <div className="userprofile-detail-card">
                          {isCurrentUserProfile() && (
                            <button
                              type="button"
                              className="userprofile-detail-visibility-btn"
                              onClick={() => toggleFieldVisibility('phone')}
                              title={detailVisibility.phone ? (t("userprofile.fields.makePrivate") || "Make private") : (t("userprofile.fields.makePublic") || "Make public")}
                            >
                              {detailVisibility.phone ? (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M1 12C2.8 7.33333 6.93333 5 12 5C17.0667 5 21.2 7.33333 23 12C21.2 16.6667 17.0667 19 12 19C6.93333 19 2.8 16.6667 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                                </svg>
                              ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M17.94 17.94C16.26 18.9672 14.2938 19.5007 12.29 19.5C7.22333 19.5 3.09 17.1667 1.29 12.5C2.1 10.34 3.39 8.52 5.02 7.14M9.88 5.37C10.662 5.12257 11.4745 4.9989 12.29 5C17.3567 5 21.49 7.33333 23.29 12C22.6637 13.6722 21.6991 15.1984 20.46 16.49M10.59 10.59C10.214 10.966 10.0028 11.4748 10.0028 12.005C10.0028 12.5352 10.214 13.044 10.59 13.42C10.966 13.796 11.4748 14.0072 12.005 14.0072C12.5352 14.0072 13.044 13.796 13.42 13.42M1 1L23 23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              )}
                            </button>
                          )}
                          <div className="userprofile-detail-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M3 5C3 3.89543 3.89543 3 5 3H8.27924C8.70967 3 9.09181 3.27543 9.22792 3.68377L10.7257 8.17721C10.8831 8.64932 10.6694 9.16531 10.2243 9.38787L7.96701 10.5165C9.06925 12.9612 11.0388 14.9308 13.4835 16.033L14.6121 13.7757C14.8347 13.3306 15.3507 13.1169 15.8228 13.2743L20.3162 14.7721C20.7246 14.9082 21 15.2903 21 15.7208V19C21 20.1046 20.1046 21 19 21H18C9.71573 21 3 14.2843 3 6V5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </div>
                          <div className="userprofile-detail-text">
                            <span className="userprofile-detail-label">{t("userprofile.fields.phone") || "Phone"}</span>
                            <span className="userprofile-detail-value">
                              {(!detailVisibility.phone && !isCurrentUserProfile()) ? (t("userprofile.fields.privateLabel") || "Private") : (profileData.phone || profileData.contact)}
                            </span>
                            {!detailVisibility.phone && isCurrentUserProfile() && (
                              <span className="userprofile-detail-privacy private">{t("userprofile.fields.privateLabel") || "Private"}</span>
                            )}
                          </div>
                        </div>
                      )}

                      {profileData.user_type === 'FIRM' && (
                        <>
                          <div className="userprofile-detail-card">
                            {isCurrentUserProfile() && (
                              <button
                                type="button"
                                className="userprofile-detail-visibility-btn"
                                onClick={() => toggleFieldVisibility('name_en')}
                                title={detailVisibility.name_en ? (t("userprofile.fields.makePrivate") || "Make private") : (t("userprofile.fields.makePublic") || "Make public")}
                              >
                                {detailVisibility.name_en ? (
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1 12C2.8 7.33333 6.93333 5 12 5C17.0667 5 21.2 7.33333 23 12C21.2 16.6667 17.0667 19 12 19C6.93333 19 2.8 16.6667 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                                  </svg>
                                ) : (
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M17.94 17.94C16.26 18.9672 14.2938 19.5007 12.29 19.5C7.22333 19.5 3.09 17.1667 1.29 12.5C2.1 10.34 3.39 8.52 5.02 7.14M9.88 5.37C10.662 5.12257 11.4745 4.9989 12.29 5C17.3567 5 21.49 7.33333 23.29 12C22.6637 13.6722 21.6991 15.1984 20.46 16.49M10.59 10.59C10.214 10.966 10.0028 11.4748 10.0028 12.005C10.0028 12.5352 10.214 13.044 10.59 13.42C10.966 13.796 11.4748 14.0072 12.005 14.0072C12.5352 14.0072 13.044 13.796 13.42 13.42M1 1L23 23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                )}
                              </button>
                            )}
                            <div className="userprofile-detail-icon">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M19 21V5C19 3.89543 18.1046 3 17 3H7C5.89543 3 5 3.89543 5 5V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M12 7H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M12 11H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M12 15H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M7 11H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M7 15H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M15 11H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M15 15H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </div>
                            <div className="userprofile-detail-text">
                              <span className="userprofile-detail-label">{t("userprofile.fields.nameEn") || "Company Name (EN)"}</span>
                              <span className="userprofile-detail-value">
                                {(!detailVisibility.name_en && !isCurrentUserProfile()) ? (t("userprofile.fields.privateLabel") || "Private") : (profileData.name_en || t("userprofile.fields.notProvided"))}
                              </span>
                              {!detailVisibility.name_en && isCurrentUserProfile() && (
                                <span className="userprofile-detail-privacy private">{t("userprofile.fields.privateLabel") || "Private"}</span>
                              )}
                            </div>
                          </div>

                          <div className="userprofile-detail-card">
                            {isCurrentUserProfile() && (
                              <button
                                type="button"
                                className="userprofile-detail-visibility-btn"
                                onClick={() => toggleFieldVisibility('legal_number')}
                                title={detailVisibility.legal_number ? (t("userprofile.fields.makePrivate") || "Make private") : (t("userprofile.fields.makePublic") || "Make public")}
                              >
                                {detailVisibility.legal_number ? (
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1 12C2.8 7.33333 6.93333 5 12 5C17.0667 5 21.2 7.33333 23 12C21.2 16.6667 17.0667 19 12 19C6.93333 19 2.8 16.6667 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                                  </svg>
                                ) : (
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M17.94 17.94C16.26 18.9672 14.2938 19.5007 12.29 19.5C7.22333 19.5 3.09 17.1667 1.29 12.5C2.1 10.34 3.39 8.52 5.02 7.14M9.88 5.37C10.662 5.12257 11.4745 4.9989 12.29 5C17.3567 5 21.49 7.33333 23.29 12C22.6637 13.6722 21.6991 15.1984 20.46 16.49M10.59 10.59C10.214 10.966 10.0028 11.4748 10.0028 12.005C10.0028 12.5352 10.214 13.044 10.59 13.42C10.966 13.796 11.4748 14.0072 12.005 14.0072C12.5352 14.0072 13.044 13.796 13.42 13.42M1 1L23 23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                )}
                              </button>
                            )}
                            <div className="userprofile-detail-icon">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </div>
                            <div className="userprofile-detail-text">
                              <span className="userprofile-detail-label">{t("userprofile.fields.legalNumber") || "Legal Number"}</span>
                              <span className="userprofile-detail-value">
                                {(!detailVisibility.legal_number && !isCurrentUserProfile()) ? (t("userprofile.fields.privateLabel") || "Private") : (profileData.legal_number || t("userprofile.fields.notProvided"))}
                              </span>
                              {!detailVisibility.legal_number && isCurrentUserProfile() && (
                                <span className="userprofile-detail-privacy private">{t("userprofile.fields.privateLabel") || "Private"}</span>
                              )}
                            </div>
                          </div>

                          <div className="userprofile-detail-card">
                            {isCurrentUserProfile() && (
                              <button
                                type="button"
                                className="userprofile-detail-visibility-btn"
                                onClick={() => toggleFieldVisibility('contact_info')}
                                title={detailVisibility.contact_info ? (t("userprofile.fields.makePrivate") || "Make private") : (t("userprofile.fields.makePublic") || "Make public")}
                              >
                                {detailVisibility.contact_info ? (
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1 12C2.8 7.33333 6.93333 5 12 5C17.0667 5 21.2 7.33333 23 12C21.2 16.6667 17.0667 19 12 19C6.93333 19 2.8 16.6667 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                                  </svg>
                                ) : (
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M17.94 17.94C16.26 18.9672 14.2938 19.5007 12.29 19.5C7.22333 19.5 3.09 17.1667 1.29 12.5C2.1 10.34 3.39 8.52 5.02 7.14M9.88 5.37C10.662 5.12257 11.4745 4.9989 12.29 5C17.3567 5 21.49 7.33333 23.29 12C22.6637 13.6722 21.6991 15.1984 20.46 16.49M10.59 10.59C10.214 10.966 10.0028 11.4748 10.0028 12.005C10.0028 12.5352 10.214 13.044 10.59 13.42C10.966 13.796 11.4748 14.0072 12.005 14.0072C12.5352 14.0072 13.044 13.796 13.42 13.42M1 1L23 23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                )}
                              </button>
                            )}
                            <div className="userprofile-detail-icon">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M3 8L10.8906 13.2604C11.5624 13.7083 12.4376 13.7083 13.1094 13.2604L21 8M5 19H19C20.1046 19 21 18.1046 21 17V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V17C3 18.1046 3.89543 19 5 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </div>
                            <div className="userprofile-detail-text">
                              <span className="userprofile-detail-label">{t("userprofile.fields.contactInfo") || "Contact information"}</span>
                              <span className="userprofile-detail-value">
                                {(!detailVisibility.contact_info && !isCurrentUserProfile()) ? (t("userprofile.fields.privateLabel") || "Private") : (profileData.contact_info || t("userprofile.fields.notProvided"))}
                              </span>
                              {!detailVisibility.contact_info && isCurrentUserProfile() && (
                                <span className="userprofile-detail-privacy private">{t("userprofile.fields.privateLabel") || "Private"}</span>
                              )}
                            </div>
                          </div>

                          <div className="userprofile-detail-card">
                            {isCurrentUserProfile() && (
                              <button
                                type="button"
                                className="userprofile-detail-visibility-btn"
                                onClick={() => toggleFieldVisibility('address')}
                                title={detailVisibility.address ? (t("userprofile.fields.makePrivate") || "Make private") : (t("userprofile.fields.makePublic") || "Make public")}
                              >
                                {detailVisibility.address ? (
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1 12C2.8 7.33333 6.93333 5 12 5C17.0667 5 21.2 7.33333 23 12C21.2 16.6667 17.0667 19 12 19C6.93333 19 2.8 16.6667 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                                  </svg>
                                ) : (
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M17.94 17.94C16.26 18.9672 14.2938 19.5007 12.29 19.5C7.22333 19.5 3.09 17.1667 1.29 12.5C2.1 10.34 3.39 8.52 5.02 7.14M9.88 5.37C10.662 5.12257 11.4745 4.9989 12.29 5C17.3567 5 21.49 7.33333 23.29 12C22.6637 13.6722 21.6991 15.1984 20.46 16.49M10.59 10.59C10.214 10.966 10.0028 11.4748 10.0028 12.005C10.0028 12.5352 10.214 13.044 10.59 13.42C10.966 13.796 11.4748 14.0072 12.005 14.0072C12.5352 14.0072 13.044 13.796 13.42 13.42M1 1L23 23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                )}
                              </button>
                            )}
                            <div className="userprofile-detail-icon">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 21C12 21 7 16 7 9C7 7.67392 7.52678 6.40215 8.46447 5.46447C9.40215 4.52678 10.6739 4 12 4C13.3261 4 14.5979 4.52678 15.5355 5.46447C16.4732 6.40215 17 7.67392 17 9C17 16 12 21 12 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <circle cx="12" cy="9" r="2" stroke="currentColor" strokeWidth="2" />
                              </svg>
                            </div>
                            <div className="userprofile-detail-text">
                              <span className="userprofile-detail-label">{t("userprofile.fields.address") || "Address"}</span>
                              <span className="userprofile-detail-value">
                                {(!detailVisibility.address && !isCurrentUserProfile()) ? (t("userprofile.fields.privateLabel") || "Private") : (profileData.address || t("userprofile.fields.notProvided"))}
                              </span>
                              {!detailVisibility.address && isCurrentUserProfile() && (
                                <span className="userprofile-detail-privacy private">{t("userprofile.fields.privateLabel") || "Private"}</span>
                              )}
                            </div>
                          </div>
                        </>
                      )}

                      {profileData.user_type !== 'FIRM' && profileData.gender && (
                        <div className="userprofile-detail-card">
                          {isCurrentUserProfile() && (
                            <button
                              type="button"
                              className="userprofile-detail-visibility-btn"
                              onClick={() => toggleFieldVisibility('gender')}
                              title={detailVisibility.gender ? (t("userprofile.fields.makePrivate") || "Make private") : (t("userprofile.fields.makePublic") || "Make public")}
                            >
                              {detailVisibility.gender ? (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M1 12C2.8 7.33333 6.93333 5 12 5C17.0667 5 21.2 7.33333 23 12C21.2 16.6667 17.0667 19 12 19C6.93333 19 2.8 16.6667 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                                </svg>
                              ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M17.94 17.94C16.26 18.9672 14.2938 19.5007 12.29 19.5C7.22333 19.5 3.09 17.1667 1.29 12.5C2.1 10.34 3.39 8.52 5.02 7.14M9.88 5.37C10.662 5.12257 11.4745 4.9989 12.29 5C17.3567 5 21.49 7.33333 23.29 12C22.6637 13.6722 21.6991 15.1984 20.46 16.49M10.59 10.59C10.214 10.966 10.0028 11.4748 10.0028 12.005C10.0028 12.5352 10.214 13.044 10.59 13.42C10.966 13.796 11.4748 14.0072 12.005 14.0072C12.5352 14.0072 13.044 13.796 13.42 13.42M1 1L23 23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              )}
                            </button>
                          )}
                          <div className="userprofile-detail-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
                              <path d="M12 11V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                              <path d="M9 18H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                          </div>
                          <div className="userprofile-detail-text">
                            <span className="userprofile-detail-label">{t("userprofile.fields.gender") || "Gender"}</span>
                            <span className="userprofile-detail-value">
                              {(!detailVisibility.gender && !isCurrentUserProfile()) ? (t("userprofile.fields.privateLabel") || "Private") : (
                                profileData.gender 
                                  ? t(`userprofile.genderOptions.${profileData.gender === 'Prefer not to say' ? 'preferNot' : profileData.gender.toLowerCase()}`, profileData.gender) 
                                  : profileData.gender
                              )}
                            </span>
                            {!detailVisibility.gender && isCurrentUserProfile() && (
                              <span className="userprofile-detail-privacy private">{t("userprofile.fields.privateLabel") || "Private"}</span>
                            )}
                          </div>
                        </div>
                      )}
                      {profileData.user_type !== 'FIRM' && (profileData.age || profileData.age === 0) && (
                        <div className="userprofile-detail-card">
                          {isCurrentUserProfile() && (
                            <button
                              type="button"
                              className="userprofile-detail-visibility-btn"
                              onClick={() => toggleFieldVisibility('age')}
                              title={detailVisibility.age ? (t("userprofile.fields.makePrivate") || "Make private") : (t("userprofile.fields.makePublic") || "Make public")}
                            >
                              {detailVisibility.age ? (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M1 12C2.8 7.33333 6.93333 5 12 5C17.0667 5 21.2 7.33333 23 12C21.2 16.6667 17.0667 19 12 19C6.93333 19 2.8 16.6667 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                                </svg>
                              ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M17.94 17.94C16.26 18.9672 14.2938 19.5007 12.29 19.5C7.22333 19.5 3.09 17.1667 1.29 12.5C2.1 10.34 3.39 8.52 5.02 7.14M9.88 5.37C10.662 5.12257 11.4745 4.9989 12.29 5C17.3567 5 21.49 7.33333 23.29 12C22.6637 13.6722 21.6991 15.1984 20.46 16.49M10.59 10.59C10.214 10.966 10.0028 11.4748 10.0028 12.005C10.0028 12.5352 10.214 13.044 10.59 13.42C10.966 13.796 11.4748 14.0072 12.005 14.0072C12.5352 14.0072 13.044 13.796 13.42 13.42M1 1L23 23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              )}
                            </button>
                          )}
                          <div className="userprofile-detail-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                              <path d="M3 10H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                              <path d="M8 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                              <path d="M16 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                          </div>
                          <div className="userprofile-detail-text">
                            <span className="userprofile-detail-label">{t("userprofile.fields.age") || "Age"}</span>
                            <span className="userprofile-detail-value">
                              {(!detailVisibility.age && !isCurrentUserProfile()) ? (t("userprofile.fields.privateLabel") || "Private") : profileData.age} {typeof profileData.age === 'number' && (t("userprofile.fields.years") || "years")}
                            </span>
                            {!detailVisibility.age && isCurrentUserProfile() && (
                              <span className="userprofile-detail-privacy private">{t("userprofile.fields.privateLabel") || "Private"}</span>
                            )}
                          </div>
                        </div>
                      )}
                      {profileData.country && (
                        <div className="userprofile-detail-card">
                          {isCurrentUserProfile() && (
                            <button
                              type="button"
                              className="userprofile-detail-visibility-btn"
                              onClick={() => toggleFieldVisibility('country')}
                              title={detailVisibility.country ? (t("userprofile.fields.makePrivate") || "Make private") : (t("userprofile.fields.makePublic") || "Make public")}
                            >
                              {detailVisibility.country ? (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M1 12C2.8 7.33333 6.93333 5 12 5C17.0667 5 21.2 7.33333 23 12C21.2 16.6667 17.0667 19 12 19C6.93333 19 2.8 16.6667 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                                </svg>
                              ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M17.94 17.94C16.26 18.9672 14.2938 19.5007 12.29 19.5C7.22333 19.5 3.09 17.1667 1.29 12.5C2.1 10.34 3.39 8.52 5.02 7.14M9.88 5.37C10.662 5.12257 11.4745 4.9989 12.29 5C17.3567 5 21.49 7.33333 23.29 12C22.6637 13.6722 21.6991 15.1984 20.46 16.49M10.59 10.59C10.214 10.966 10.0028 11.4748 10.0028 12.005C10.0028 12.5352 10.214 13.044 10.59 13.42C10.966 13.796 11.4748 14.0072 12.005 14.0072C12.5352 14.0072 13.044 13.796 13.42 13.42M1 1L23 23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              )}
                            </button>
                          )}
                          <div className="userprofile-detail-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M21 10C21 15.5228 16.9706 20 12 20C7.02944 20 3 15.5228 3 10C3 4.47715 7.02944 0 12 0C16.9706 0 21 4.47715 21 10Z" stroke="currentColor" strokeWidth="2" />
                              <path d="M3 10H21" stroke="currentColor" strokeWidth="2" />
                              <path d="M12 0C14 3 15 6.5 15 10C15 13.5 14 17 12 20C10 17 9 13.5 9 10C9 6.5 10 3 12 0Z" stroke="currentColor" strokeWidth="2" />
                            </svg>
                          </div>
                          <div className="userprofile-detail-text">
                            <span className="userprofile-detail-label">{t("userprofile.fields.country") || "Country"}</span>
                            <span className="userprofile-detail-value">
                              {(!detailVisibility.country && !isCurrentUserProfile()) ? (t("userprofile.fields.privateLabel") || "Private") : profileData.country}
                            </span>
                            {!detailVisibility.country && isCurrentUserProfile() && (
                              <span className="userprofile-detail-privacy private">{t("userprofile.fields.privateLabel") || "Private"}</span>
                            )}
                          </div>
                        </div>
                      )}
                      {profileData.city && (
                        <div className="userprofile-detail-card">
                          {isCurrentUserProfile() && (
                            <button
                              type="button"
                              className="userprofile-detail-visibility-btn"
                              onClick={() => toggleFieldVisibility('city')}
                              title={detailVisibility.city ? (t("userprofile.fields.makePrivate") || "Make private") : (t("userprofile.fields.makePublic") || "Make public")}
                            >
                              {detailVisibility.city ? (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M1 12C2.8 7.33333 6.93333 5 12 5C17.0667 5 21.2 7.33333 23 12C21.2 16.6667 17.0667 19 12 19C6.93333 19 2.8 16.6667 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                                </svg>
                              ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M17.94 17.94C16.26 18.9672 14.2938 19.5007 12.29 19.5C7.22333 19.5 3.09 17.1667 1.29 12.5C2.1 10.34 3.39 8.52 5.02 7.14M9.88 5.37C10.662 5.12257 11.4745 4.9989 12.29 5C17.3567 5 21.49 7.33333 23.29 12C22.6637 13.6722 21.6991 15.1984 20.46 16.49M10.59 10.59C10.214 10.966 10.0028 11.4748 10.0028 12.005C10.0028 12.5352 10.214 13.044 10.59 13.42C10.966 13.796 11.4748 14.0072 12.005 14.0072C12.5352 14.0072 13.044 13.796 13.42 13.42M1 1L23 23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              )}
                            </button>
                          )}
                          <div className="userprofile-detail-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 2C8.68629 2 6 4.68629 6 8C6 12.25 12 18 12 18C12 18 18 12.25 18 8C18 4.68629 15.3137 2 12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              <circle cx="12" cy="8" r="2.5" stroke="currentColor" strokeWidth="2" />
                            </svg>
                          </div>
                          <div className="userprofile-detail-text">
                            <span className="userprofile-detail-label">{t("userprofile.fields.city") || "City"}</span>
                            <span className="userprofile-detail-value">
                              {(!detailVisibility.city && !isCurrentUserProfile()) ? (t("userprofile.fields.privateLabel") || "Private") : profileData.city}
                            </span>
                            {!detailVisibility.city && isCurrentUserProfile() && (
                              <span className="userprofile-detail-privacy private">{t("userprofile.fields.privateLabel") || "Private"}</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 2 && (
            <div className="userprofile-tab-panel">
              <BuddiesGridTab 
                userId={profileData?.id}
                currentUser={user}
                isCurrentUserProfile={isCurrentUserProfile()}
                accentColor={userTypeColor}
              />
            </div>
          )}

          {activeTab === 3 && (
            <div className="userprofile-tab-panel">
              {/* Pets Section */}
              <div className="userprofile-section-card">
                <div className="userprofile-section-header">
                  <h2 className="userprofile-section-title">
                    {t("userprofile.sections.myPets")}
                    {isCurrentUserProfile() && (
                      <button
                        className="userprofile-add-pet-btn"
                        onClick={() => navigate('/pets/add', { state: { fromProfile: true } })}
                        title={t("userprofile.addPet") || "Add Pet"}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    )}
                  </h2>
                  {profileData.pets && profileData.pets.length > 0 && (
                    <div className="userprofile-pets-actions">
                      {/* Search toggle button */}
                      <button
                        type="button"
                        className="userprofile-pets-search-toggle-btn"
                        onClick={() => {
                          setIsPetsSearchOpen((prev) => !prev)
                          // Reset query when closing
                          if (isPetsSearchOpen) {
                            setPetsSearchQuery("")
                          }
                        }}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle
                            cx="11"
                            cy="11"
                            r="6"
                            stroke="currentColor"
                            strokeWidth="2"
                          />
                          <line
                            x1="15.5"
                            y1="15.5"
                            x2="20"
                            y2="20"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>

                      {/* Sliding search bar */}
                      <div
                        className={`
                            userprofile-pets-search-wrapper
                            ${isPetsSearchOpen ? "open" : ""}
                          `}
                      >
                        <input
                          type="text"
                          className="userprofile-pets-search-input"
                          placeholder={t("userprofile.sections.searchPetsPlaceholder") || "Search pets..."}
                          value={petsSearchQuery}
                          onChange={(e) => setPetsSearchQuery(e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>
                {profileData.pets && profileData.pets.length > 0 ? (
                  <div className="userprofile-pets-grid">
                    {(profileData.pets || [])
                      // Filter by prefix on name; only pets whose name starts with the query (case-insensitive)
                      .filter((pet) => {
                        if (!petsSearchQuery.trim()) return true
                        const name = (pet.name || "").toString()
                        const query = petsSearchQuery.trim().toLowerCase()
                        return name.toLowerCase().startsWith(query)
                      })
                      .map((pet) => (
                        <div
                          key={pet.id}
                          className="userprofile-pet-card"
                          onClick={() => {
                            if (isCurrentUserProfile()) {
                              navigate(`/pets/${pet.id}/view`, { state: { fromProfile: true, fromProfileTab: activeTab } })
                            } else {
                              navigate(`/pets/view/others/${pet.id}`, { state: { fromProfile: true, fromProfileTab: activeTab } })
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <img
                            src={pet.avatar || "/images/no-image-pet.png"}
                            alt={pet.name}
                            className="userprofile-pet-image"
                          />
                          <p className="userprofile-pet-name">{pet.name}</p>
                          {pet.type && (
                            <p className="userprofile-pet-type">{pet.type}</p>
                          )}
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="userprofile-empty-state">
                    <span style={{ fontSize: '32px' }}>🐾</span>
                    <p>{t("userprofile.sections.noPets")}</p>
                  </div>
                )}
                {hasMorePets && (
                  <div className="userprofile-load-more-container" style={{ marginTop: '20px', textAlign: 'center' }}>
                    <button
                      className="userprofile-load-more-btn"
                      onClick={loadMorePets}
                      disabled={isLoadingMorePets}
                      style={{
                        padding: '12px 24px',
                        backgroundColor: '#8548A8',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: isLoadingMorePets ? 'not-allowed' : 'pointer',
                        opacity: isLoadingMorePets ? 0.6 : 1,
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      {isLoadingMorePets
                        ? (t("userprofile.loading") || "Loading...")
                        : (t("userprofile.loadMorePets") || "Load More Pets")}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 4 && (
            <div className="userprofile-tab-panel">
              {/* Communities Section */}
              <div className="userprofile-section-card">
                <div className="userprofile-section-header">
                  <h2 className="userprofile-section-title">
                    {t("userprofile.sections.communities")}
                    {isCurrentUserProfile() && (
                      <button
                        className="userprofile-add-pet-btn"
                        onClick={() => setIsCreateCommunityModalOpen(true)}
                        title={t("userprofile.addCommunity") || "Create Community"}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    )}
                  </h2>
                  {communities.length > 0 && (
                    <div className="userprofile-pets-actions">
                      {/* Search toggle button */}
                      <button
                        type="button"
                        className="userprofile-pets-search-toggle-btn"
                        onClick={() => {
                          setIsCommunitiesSearchOpen((prev) => !prev)
                          // Reset query when closing
                          if (isCommunitiesSearchOpen) {
                            setCommunitiesSearchQuery("")
                          }
                        }}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle
                            cx="11"
                            cy="11"
                            r="6"
                            stroke="currentColor"
                            strokeWidth="2"
                          />
                          <line
                            x1="15.5"
                            y1="15.5"
                            x2="20"
                            y2="20"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>

                      {/* Sliding search bar */}
                      <div
                        className={`
                            userprofile-pets-search-wrapper
                            ${isCommunitiesSearchOpen ? "open" : ""}
                          `}
                      >
                        <input
                          type="text"
                          className="userprofile-pets-search-input"
                          placeholder={t("userprofile.sections.searchCommunitiesPlaceholder") || "Search communities..."}
                          value={communitiesSearchQuery}
                          onChange={(e) => setCommunitiesSearchQuery(e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>
                {isLoadingCommunities ? (
                  <p>{t("userprofile.sections.loadingCommunities")}</p>
                ) : communities.length > 0 ? (
                  <div className="userprofile-communities-grid">
                    {(communities || [])
                      // Filter by prefix on name; only communities whose name starts with the query (case-insensitive)
                      .filter((community) => {
                        if (!communitiesSearchQuery.trim()) return true
                        const name = (community.name || "").toString()
                        const query = communitiesSearchQuery.trim().toLowerCase()
                        return name.toLowerCase().startsWith(query)
                      })
                      .map(community => (
                        <div
                          key={community.id}
                          className="userprofile-community-card"
                          onClick={() => navigate(`/social/channels/${community.id}`, { state: { fromProfile: true, fromProfileTab: activeTab } })}
                          style={{ cursor: 'pointer' }}
                        >
                          <img
                            src={community.profileImage}
                            alt={community.name}
                            className="userprofile-community-image"
                          />
                          <h3 className="userprofile-community-name">{community.name}</h3>
                          {community.description && (
                            <p className="userprofile-community-description">{community.description}</p>
                          )}
                          <div className="userprofile-community-stats">
                            <span>{community.memberCount} {t("userprofile.sections.members")}</span>
                            <span>{community.postCount} {t("userprofile.sections.posts")}</span>
                          </div>
                          <div className="userprofile-community-action">
                            {t("common.view") || "View"}
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="userprofile-empty-state">
                    <span style={{ fontSize: '32px' }}>🏘️</span>
                    <p>{t("userprofile.sections.noPublicCommunities")}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 5 && (
            <div className="userprofile-tab-panel">
              <div className="userprofile-section-card">
                {isLoadingMedia ? (
                  <div className="userprofile-media-loading">
                    <p>{t("common.loading") || "Loading..."}</p>
                  </div>
                ) : mediaItems.length > 0 ? (
                  <div className="userprofile-media-grid">
                    {mediaItems.map((mediaItem, index) => {
                      const mediaUrl = resolveImageUrl(
                        mediaItem.url,
                        mediaItem.path,
                        mediaItem.file_path,
                        mediaItem.media_path
                      )
                      const isVideo = mediaItem.file_type === 'video' ||
                        mediaItem.type === 'video' ||
                        (typeof mediaUrl === 'string' &&
                          (mediaUrl.includes('.mp4') ||
                            mediaUrl.includes('.mov') ||
                            mediaUrl.includes('.webm')))

                      return (
                        <div
                          key={mediaItem.id || index}
                          className="userprofile-media-item"
                          onClick={() => {
                            if (mediaItem.postId) {
                              // Open the post modal when clicking on media
                              openPostModal(mediaItem.postId);
                              // Also update URL to include postId
                              const basePath = getBaseProfilePath();
                              const tabName = REVERSE_TAB_MAP[activeTab];
                              navigate(`${basePath}/${tabName}/${mediaItem.postId}`);
                            }
                          }}
                        >
                          {isVideo ? (
                            <video
                              src={mediaUrl}
                              className="userprofile-media-thumbnail"
                              muted
                            />
                          ) : (
                            <img
                              src={mediaUrl}
                              alt={mediaItem.postContent || "Post media"}
                              className="userprofile-media-thumbnail"
                              loading="lazy"
                            />
                          )}
                          {isVideo && (
                            <div className="userprofile-media-play-icon">
                              ▶
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="userprofile-media-empty">
                    <span style={{ fontSize: '40px', marginBottom: '16px', opacity: 0.5 }}>📷</span>
                    <p>{t("userprofile.noMedia") || "No media posted yet"}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 6 && (
            <div className="userprofile-tab-panel">
              <div className="userprofile-section-card">
                <div className="userprofile-posts-container">
                  {isLoadingPosts ? (
                    <div className="userprofile-posts-loading">
                      <p>{t("common.loading") || "Loading posts..."}</p>
                    </div>
                  ) : posts && posts.length > 0 ? (
                    <>
                      <PostFeed
                        posts={posts}
                        currentUser={user}
                        onUpdatePost={handleUpdatePost}
                        onDeletePost={handleDeletePost}
                        onEditPost={handleEditPost}
                        onLikeToggle={handleLikeToggle}
                        onCommentAdded={handleCommentAdded}
                        onLoadMore={null}
                        hasMorePosts={false}
                        isLoading={false}
                      />
                      {hasMorePosts && (
                        <div className="userprofile-load-more-container">
                          <button
                            className="userprofile-load-more-btn"
                            onClick={loadMorePosts}
                            disabled={isLoadingMorePosts}
                          >
                            {isLoadingMorePosts ? (
                              <>
                                <span className="userprofile-load-more-spinner"></span>
                                {t("common.loading") || "Loading..."}
                              </>
                            ) : (
                              t("userprofile.loadMorePosts") || "Load More Posts"
                            )}
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="userprofile-posts-empty">
                      <span style={{ fontSize: '40px', marginBottom: '16px', opacity: 0.5 }}>📝</span>
                      <p>{t("userprofile.noPosts") || "No posts yet"}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Connects Modal removed, now a tab */}

      {/* Create Community Modal */}
      <CreateChannelModal
        isOpen={isCreateCommunityModalOpen}
        onClose={() => setIsCreateCommunityModalOpen(false)}
        onChannelCreated={(newChannel) => {
          setCommunities(prev => [newChannel, ...prev])
          setCommunitiesCount(prev => prev + 1)
          setIsCreateCommunityModalOpen(false)
        }}
      />

      {/* Post Modal */}
      <PostModal
        isOpen={isCreateEditModalOpen}
        onClose={closeCreateEditModal}
        mode={editingPostData ? "edit" : "create"}
        editingPost={editingPostData}
        currentUser={user}
        onCreatePost={handleCreatePost}
        onUpdatePost={handleUpdatePost}
        initialPrivacy={selectedPrivacy}
        pendingFileType={pendingFileType}
      />
    </div>
  )
}

export default UserProfile

